/**
 * 画布文档模型（P0-1d）
 *
 * 画布是全站总线（蓝图 D1），其余模块都以节点形式挂在其上。
 * 字段命名我方自定，语义覆盖竞品能力（蓝图 §11.1「融合而非照抄」）。
 */

export type NodeKind =
  | 'text'
  | 'image'
  | 'shot'
  | 'video'
  | 'audio'
  | 'subject'
  | 'timeline'
  | 'director'
  | 'skill'
  | 'group';

/**
 * 节点三态。竞品用 asset/generator/variant，且实测有个不一致：
 * `mode='asset'` 的图片节点**不弹生成参数面板**，用户无法就地改参数调生成，行为不可预期。
 * 我方把「能否编辑参数」抽成显式函数 canEditParams()，规则唯一且可测。
 */
export type NodeMode =
  /** 外部导入或资产引用（上传、从资产库选、导演台截图落成的图） */
  | 'source'
  /** 待生成：携带 params，由后端产出 media */
  | 'generator'
  /** 派生产物：由上游节点计算得到，本身不带参数 */
  | 'derived';

export interface Vec2 {
  x: number;
  y: number;
}

export interface MediaRef {
  mediaId: string;
  url: string;
  thumbnail?: string | undefined;
  width?: number | undefined;
  height?: number | undefined;
  durationSec?: number | undefined;
}

/** 生成参数。字段集合对齐竞品，命名统一为我方规范（真布尔、真数字） */
export interface GenerationParams {
  model: string;
  /** 生成能力，取代竞品语义模糊的 taskType */
  capability: 'textToImage' | 'imageToImage' | 'textToVideo' | 'imageToVideo' | 'referenceToVideo' | 'videoEdit' | 'tts';
  aspectRatio: '16:9' | '9:16' | '1:1' | '3:4' | '4:3' | '21:9';
  resolution: string;
  durationSec: number;
  count: number;
  /** 提示词，内含 @@<nodeId> 形式的上游引用 */
  prompt: string;
  /** 负向提示词（图像类常用：不想要什么） */
  negativePrompt?: string | undefined;
  enhancePrompt: boolean;
}

export interface CanvasNode {
  /** 唯一键。竞品模板里出现过重名对象，故 name 只作展示，一切以 id 为准 */
  id: string;
  kind: NodeKind;
  mode: NodeMode;
  title: string;
  position: Vec2;
  media?: MediaRef | undefined;
  /** 一次生成的多张候选（非破坏）：节点卡显主图 media，面板显候选条供挑选 */
  variants?: MediaRef[] | undefined;
  params?: GenerationParams | undefined;
  /** kind 专属负载（如 director 节点的工程引用、subject 的实体 id） */
  payload?: unknown;
}

export interface CanvasEdge {
  id: string;
  from: string;
  to: string;
}

export interface CanvasViewport {
  x: number;
  y: number;
  zoom: number;
}

export interface CanvasDoc {
  schemaVersion: number;
  id: string;
  title: string;
  updatedAt: string;
  nodes: CanvasNode[];
  edges: CanvasEdge[];
  viewport?: CanvasViewport | undefined;
}

export const CANVAS_SCHEMA_VERSION = 1;

/** 节点类型→中文标签（供“添加节点”菜单与节点卡展示） */
export const NODE_KIND_LABELS: Record<NodeKind, string> = {
  text: '文本',
  image: '分镜关键帧',
  shot: '分镜',
  video: '视频',
  audio: '音频',
  subject: '角色',
  timeline: '时间线',
  director: '导演台',
  skill: '技能',
  group: '组合',
};

/** 新建一个默认节点（source 态，标题取类型标签）；id/位置由调用方给定 */
export function createNode(kind: NodeKind, id: string, position: Vec2, title?: string): CanvasNode {
  return { id, kind, mode: 'source', title: title ?? NODE_KIND_LABELS[kind], position };
}

/** 图像生成面板的可选参数（mock 阶段先固定几项，接真供商时换成模型自声明的 allow-list） */
export const IMAGE_ASPECT_RATIOS = ['16:9', '9:16', '1:1', '3:4', '4:3', '21:9'] as const;
export const IMAGE_RESOLUTIONS = ['480P', '720P', '1080P', '2K'] as const;
export const IMAGE_COUNTS = [1, 2, 3, 4] as const;

/** 造一个图像生成节点：kind image、mode generator、携默认 params（过 generator 校验）。 */
export function createImageGenNode(id: string, position: Vec2): CanvasNode {
  const params: GenerationParams = {
    model: 'wan2.7-image',
    capability: 'textToImage',
    aspectRatio: '16:9',
    resolution: '2K',
    durationSec: 0,
    count: 1,
    prompt: '',
    negativePrompt: '',
    enhancePrompt: false,
  };
  return { id, kind: 'image', mode: 'generator', title: '分镜关键帧', position, params };
}

/** 造一个分镜视频生成节点：kind video、mode generator、携 imageToVideo 默认 params。 */
export function createVideoGenNode(id: string, position: Vec2, title = '分镜视频'): CanvasNode {
  const params: GenerationParams = {
    model: 'wan2.7-video',
    capability: 'imageToVideo',
    aspectRatio: '16:9',
    resolution: '1080P',
    durationSec: 5,
    count: 1,
    prompt: '',
    enhancePrompt: false,
  };
  return { id, kind: 'video', mode: 'generator', title, position, params };
}

/** 一句配音：一个角色的一句台词 + 逐句情绪；url 为合成后音频；voiceId 可临时覆盖角色绑定。 */
export interface AudioLine {
  role: string;
  text: string;
  emotion: string;
  voiceId?: string | undefined;
  url?: string | undefined;
}

/** 配音节点负载：一镜可多句（多人对话逐行）。 */
export interface AudioPayload {
  type: 'audio';
  lines: AudioLine[];
}

/** 造一个配音节点：kind audio、mode generator（靠 payload 过校验）、携多行台词。 */
export function createAudioNode(id: string, position: Vec2, lines: AudioLine[]): CanvasNode {
  const first = lines[0]?.role ?? '旁白';
  const suffix = lines.length > 1 ? ` 等${lines.length}句` : '';
  const payload: AudioPayload = { type: 'audio', lines };
  return { id, kind: 'audio', mode: 'generator', title: `配音·${first}${suffix}`, position, payload };
}

/** 改某一句（不可变）：供逐句情绪/台词/音色编辑。 */
export function updateAudioLine(p: AudioPayload, index: number, patch: Partial<AudioLine>): AudioPayload {
  return { ...p, lines: p.lines.map((l, i) => (i === index ? { ...l, ...patch } : l)) };
}

/** 读配音节点负载。 */
export function isAudioPayload(p: unknown): p is AudioPayload {
  return (
    typeof p === 'object' &&
    p !== null &&
    (p as { type?: unknown }).type === 'audio' &&
    Array.isArray((p as { lines?: unknown }).lines)
  );
}

/** 组合负载（轻量框：不做 xyflow 父子耦合，只记成员 id + 尺寸 + 主题色，稳）。 */
export interface GroupPayload {
  type: 'group';
  label: string;
  color: string;
  memberIds: string[];
  width: number;
  height: number;
}

/** 主题色候选（token 键，实色定义在 index.css 的 .gc-* ；eslint 禁硬编码 hex）。 */
export const GROUP_COLORS: string[] = ['amber', 'green', 'blue', 'pink', 'orange', 'violet'];

export function isGroupPayload(p: unknown): p is GroupPayload {
  return (
    typeof p === 'object' &&
    p !== null &&
    (p as { type?: unknown }).type === 'group' &&
    Array.isArray((p as { memberIds?: unknown }).memberIds)
  );
}

/** 造一个组合框节点：kind group、mode source（不需 params）、携成员/颜色/尺寸。 */
export function createGroupNode(id: string, position: Vec2, payload: GroupPayload): CanvasNode {
  return { id, kind: 'group', mode: 'source', title: payload.label, position, payload };
}

export function createEmptyCanvas(id: string, nowIso: string): CanvasDoc {
  return {
    schemaVersion: CANVAS_SCHEMA_VERSION,
    id,
    title: '未命名画布',
    updatedAt: nowIso,
    nodes: [],
    edges: [],
  };
}

/**
 * 「能否就地编辑生成参数」的唯一判定。
 * 规则：只有 generator 节点可编辑参数；source/derived 是既有产物，改参数无意义，
 * 但 UI 应给出明确出口（如「基于此新建生成节点」），而不是像竞品那样静默不弹面板。
 */
export function canEditParams(node: CanvasNode): boolean {
  return node.mode === 'generator';
}

/** 该节点是否可作为生成输入被下游引用 */
export function canBeReferenced(node: CanvasNode): boolean {
  return node.media !== undefined || node.mode === 'generator';
}

/** 提示词引用语法：@@<nodeId>。@@ 而非 @，避免与邮箱/账号文本冲突 */
export function mentionOf(nodeId: string): string {
  return `@@<${nodeId}>`;
}

export function nodeIdOfMention(token: string): string | null {
  const m = /^@@<([^>]+)>$/.exec(token);
  return m?.[1] ?? null;
}

export function findNode(doc: CanvasDoc, id: string): CanvasNode | undefined {
  return doc.nodes.find((n) => n.id === id);
}

export function incomingEdges(doc: CanvasDoc, nodeId: string): CanvasEdge[] {
  return doc.edges.filter((e) => e.to === nodeId);
}

export function outgoingEdges(doc: CanvasDoc, nodeId: string): CanvasEdge[] {
  return doc.edges.filter((e) => e.from === nodeId);
}

/**
 * 结构校验。返回问题列表，空数组即合法。
 * 加载工程时先校验再渲染，避免"半个坏工程把整个画布画崩"。
 */
export function validateCanvasDoc(doc: unknown): string[] {
  const problems: string[] = [];
  if (typeof doc !== 'object' || doc === null) {
    return ['画布文档不是合法对象'];
  }
  const d = doc as Partial<CanvasDoc>;

  if (typeof d.id !== 'string' || d.id === '') {
    problems.push('缺少画布 id');
  }
  if (!Array.isArray(d.nodes)) {
    problems.push('nodes 不是数组');
    return problems;
  }
  if (!Array.isArray(d.edges)) {
    problems.push('edges 不是数组');
  }

  const seen = new Set<string>();
  d.nodes.forEach((n, i) => {
    if (typeof n?.id !== 'string' || n.id === '') {
      problems.push(`第 ${i} 个节点缺少 id`);
      return;
    }
    if (seen.has(n.id)) {
      problems.push(`节点 id 重复：${n.id}`);
    }
    seen.add(n.id);
    if (typeof n.title !== 'string') {
      problems.push(`节点 ${n.id} 缺少 title`);
    }
    // 生成节点必须自带生成依据：媒体生成看 params，文本/其它生成看 payload，二者皆无才算缺参。
    if (n.mode === 'generator' && n.params === undefined && n.payload === undefined) {
      problems.push(`generator 节点 ${n.id} 缺少 params`);
    }
  });

  if (Array.isArray(d.edges)) {
    d.edges.forEach((e, i) => {
      if (typeof e?.from !== 'string' || typeof e?.to !== 'string') {
        problems.push(`第 ${i} 条连线缺少端点`);
        return;
      }
      if (!seen.has(e.from)) {
        problems.push(`连线 ${e.id ?? i} 的起点 ${e.from} 不存在`);
      }
      if (!seen.has(e.to)) {
        problems.push(`连线 ${e.id ?? i} 的终点 ${e.to} 不存在`);
      }
    });
  }

  return problems;
}
